import { useState, useCallback, useRef, type MouseEvent as ReactMouseEvent, type ReactElement, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Portal } from '@ark-ui/react';
import { SidebarItem, useSidebar } from '../../components/Sidebar';
import { Button } from '../../components/Button';
import { Icon } from '../../icons/Icon';
import { HoverCard } from '../../components/HoverCard';
import { SpaceLogo } from '../../components/SpaceLogo';
import { useIdentity } from '../../hooks/useIdentity';
import { useConversations } from '../../hooks/useConversations';
import { useSpaceInvites } from '../../hooks/spaces/useSpaceInvites';
import { useInvitationInbox } from '../../hooks/useInvitationInbox';
import { useCipherStore } from '../../hooks/useCipherStore';
import { getSpaceCipherLink } from '../../services/spaceCipherService';
import { spacePathFromSlug } from '../../pages/spaces/spaceInviteUtils';
import {
  resolveSpaceDisplayDescription,
  resolveSpaceDisplayName,
} from '../../pages/spaces/spaceMetadataCipher';
import { useToast } from '../../components/Toast';
import { useSidebarPanelDismiss } from './useSidebarPanelDismiss';
import { useSidebarListView } from './sidebarListView';
import {
  filterInvitesForListView,
  invitationNoticeIcon,
  invitationNoticeI18nKey,
  invitationNoticeKind,
} from './invitationNotice';
import {
  inviteInboxOutcomeFact,
  spaceInviteInboxFactLabel,
  spaceInviteInboxFactSections,
} from './spaceInviteInboxDetails';
import {
  GROUP_INVITE_PREVIEW_INVITED_LIMIT,
  GROUP_INVITE_PREVIEW_MEMBER_LIMIT,
  INVITE_INBOX_BUCKETS,
  INVITE_INBOX_PAGE_SIZE,
  INVITE_INBOX_SORTS,
  type InviteInboxBucket,
  type InviteInboxSort,
  type InviteInboxSortDir,
  type PublicGroupInvite,
  type PublicSpaceInvite,
  type GroupInvitePreview,
  type GroupInvitePreviewMember,
  type SpaceInvitePreview,
  type SpaceInvitePreviewMember,
  type SpaceVisibility,
} from '@adieuu/shared';
import { Tooltip } from '../../components/Tooltip';

export function ChatInvitationsSidebarButton({
  isOpen,
  onToggle,
}: {
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const { status: identityStatus } = useIdentity();
  const { counts } = useInvitationInbox();
  const { listView } = useSidebarListView();

  const isIdentityLoggedIn = identityStatus === 'logged_in';
  const filtered = filterInvitesForListView(listView, counts.pendingGroup, counts.pendingSpace);
  const pendingKind = invitationNoticeKind(filtered.groupCount, filtered.spaceCount);
  const historyTotal = counts.pending + counts.declined + counts.expired + counts.accepted;
  if (!isIdentityLoggedIn || historyTotal <= 0) return null;

  const pendingTotal = filtered.groupCount + filtered.spaceCount;
  const buttonLabel = pendingKind
    ? t(invitationNoticeI18nKey(pendingKind), { count: pendingTotal })
    : t('nav.inviteHistory');
  const icon = pendingKind ? invitationNoticeIcon(pendingKind) : 'message';

  return (
    <SidebarItem
      icon={<Icon name={icon} />}
      label={buttonLabel}
      onClick={onToggle}
      isActive={isOpen || pendingTotal > 0}
    />
  );
}

function isSpaceInviteEncrypted(invite: {
  e2ee?: boolean;
  encryptIdentity?: boolean;
}): boolean {
  return invite.e2ee === true || invite.encryptIdentity === true;
}

function SpaceInviteTypeLabel({
  visibility,
  className,
  iconClassName,
}: {
  visibility?: SpaceVisibility;
  className: string;
  iconClassName: string;
}) {
  const { t } = useTranslation();
  return (
    <span className={className}>
      <Icon name="spaces" className={iconClassName} />
      {t('spaces.invites.inbox.typeSpace', 'Space')}
      {visibility ? (
        <>
          {' · '}
          {t(`spaces.visibility.${visibility}`, visibility)}
        </>
      ) : null}
    </span>
  );
}

function SpaceInviteInboxExpiry({
  invite,
  className,
}: {
  invite: PublicSpaceInvite;
  className: string;
}) {
  const { t } = useTranslation();
  const expiry = spaceInviteInboxFactSections(invite, t).expiry;
  if (!expiry) return null;
  return <span className={className}>{expiry}</span>;
}

function SpaceInviteMeta({
  invite,
  inviterName,
}: {
  invite: PublicSpaceInvite;
  inviterName?: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <span className="sidebar-invitations-panel-item-meta">
        <SpaceInviteTypeLabel
          visibility={invite.visibility}
          className="sidebar-invitations-panel-item-type"
          iconClassName="sidebar-invitations-panel-item-type-icon"
        />
        {' · '}
        {t('spaces.memberCount', { count: invite.memberCount })}
      </span>
      {inviterName ? (
        <span className="sidebar-invitations-panel-item-inviter">
          {t('spaces.invites.inbox.invitedBy', { name: inviterName })}
        </span>
      ) : null}
    </>
  );
}

function SpaceInviteInboxRowFacts({ invite }: { invite: PublicSpaceInvite }) {
  const { t } = useTranslation();
  const sections = spaceInviteInboxFactSections(invite, t);
  const facts: { key: string; node: ReactNode }[] = [];
  if (sections.expiry) facts.push({ key: 'expiry', node: sections.expiry });
  if (sections.access) facts.push({ key: 'access', node: sections.access });
  if (sections.temporaryMembership) {
    facts.push({
      key: 'temporary',
      node: (
        <>
          <Icon name="timer" className="sidebar-invitations-panel-item-temporary-icon" />
          {sections.temporaryMembership}
        </>
      ),
    });
  }
  if (facts.length === 0) return null;
  return (
    <div className="sidebar-invitations-panel-item-facts">
      {facts.map((fact, index) => (
        <span
          key={fact.key}
          className={
            fact.key === 'temporary'
              ? 'sidebar-invitations-panel-item-temporary'
              : `sidebar-invitations-panel-item-${fact.key}`
          }
        >
          {index > 0 ? (
            <span className="sidebar-invitations-panel-item-fact-sep" aria-hidden="true">
              ·
            </span>
          ) : null}
          {fact.node}
        </span>
      ))}
    </div>
  );
}

function SpaceInviteInboxHoverFacts({ invite }: { invite: PublicSpaceInvite }) {
  const { t } = useTranslation();
  const sections = spaceInviteInboxFactSections(invite, t);
  if (!sections.access && !sections.temporaryMembership) return null;
  return (
    <>
      {sections.access && (
        <ul className="invite-group-hover-card-terms">
          <li>{sections.access}</li>
        </ul>
      )}
      {sections.temporaryMembership && (
        <div className="invite-group-hover-card-temporary">
          <Icon name="timer" className="invite-group-hover-card-temporary-icon" />
          {sections.temporaryMembership}
        </div>
      )}
    </>
  );
}

function GroupInviteInboxInlineFacts({ invite }: { invite: PublicGroupInvite }) {
  const { t } = useTranslation();
  const fact = inviteInboxOutcomeFact(invite);
  if (!fact) return null;
  const label = spaceInviteInboxFactLabel(fact, t);
  if (!label) return null;
  return (
    <span>
      {' · '}
      {label}
    </span>
  );
}

function GroupInviteInboxHoverFacts({ invite }: { invite: PublicGroupInvite }) {
  const { t } = useTranslation();
  const fact = inviteInboxOutcomeFact(invite);
  if (!fact) return null;
  const label = spaceInviteInboxFactLabel(fact, t);
  if (!label) return null;
  return (
    <ul className="invite-group-hover-card-terms">
      <li>{label}</li>
    </ul>
  );
}

function InviteGroupHoverCard({
  invite,
  livePreview,
  children,
}: {
  invite: PublicGroupInvite;
  livePreview: boolean;
  children: ReactElement;
}) {
  const { t } = useTranslation();
  const { getInvitePreview, participantProfiles } = useConversations();
  const [preview, setPreview] = useState<GroupInvitePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  const handleOpen = useCallback(async () => {
    if (!livePreview || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    const data = await getInvitePreview(invite.id);
    setPreview(data);
    setLoading(false);
  }, [getInvitePreview, invite.id, livePreview]);

  const inviterProfile = participantProfiles[invite.invitedByIdentityId];
  const snapshotInviterName = inviterProfile?.displayName ?? inviterProfile?.username;
  const othersCount = invite.memberCount - 1;
  const snapshotName = invite.hasGroupName
    ? t('conversations.invites.groupNameHidden', 'Conversation topic hidden')
    : snapshotInviterName
      ? (othersCount > 0
        ? t('conversations.invites.inviterAndOthers', {
            name: snapshotInviterName,
            count: othersCount,
            defaultValue: `${snapshotInviterName} + ${othersCount} others`,
          })
        : t('conversations.invites.inviterGroup', {
            name: snapshotInviterName,
            defaultValue: `${snapshotInviterName}'s Group`,
          }))
      : t('conversations.invites.group', 'Group');
  const recentMembers = preview
    ? preview.members.slice(0, GROUP_INVITE_PREVIEW_MEMBER_LIMIT)
    : [];
  const extraGroupMembers = preview
    ? Math.max(0, preview.memberCount - recentMembers.length)
    : 0;
  const shownInvited = preview
    ? preview.invitedMembers.slice(0, GROUP_INVITE_PREVIEW_INVITED_LIMIT)
    : [];
  const extraInvited = preview
    ? Math.max(
        0,
        (preview.invitedMemberCount ?? preview.invitedMembers.length) - shownInvited.length,
      )
    : 0;

  return (
    <HoverCard
      trigger={children}
      positioning={{ placement: 'right', gutter: 8 }}
      className="invite-group-hover-card"
      openDelay={300}
      closeDelay={200}
      onOpenChange={(details: { open: boolean }) => {
        if (details.open) void handleOpen();
      }}
    >
      {livePreview && loading && (
        <div className="invite-group-hover-card-loading">
          <span className="spinner spinner-sm" />
        </div>
      )}
      {livePreview && !loading && preview && (
        <>
          <div className="invite-group-hover-card-header">
            <span className="invite-group-hover-card-type">
              {t('conversations.invites.typeGroup', 'Group')}
            </span>
            <span className="invite-group-hover-card-name">
              {preview.hasGroupName
                ? t('conversations.invites.groupNameHidden', 'Conversation topic hidden')
                : t('conversations.invites.group', 'Group')}
            </span>
            <span className="invite-group-hover-card-count">
              {t('conversations.invites.previewMemberCount', {
                count: preview.memberCount,
                defaultValue: '{{count}} members',
              })}
            </span>
          </div>
          <div className="invite-group-hover-card-inviter">
            <span className="invite-group-hover-card-inviter-label">
              {t('conversations.invites.invitedByLabel', 'Invited by')}
            </span>
            <span className="invite-group-hover-card-inviter-name">
              {preview.invitedBy.displayName}
              {preview.invitedBy.isAdmin && (
                <span className="conversation-member-admin-badge">
                  {t('conversations.admin', 'Admin')}
                </span>
              )}
            </span>
          </div>
          <div className="invite-group-hover-card-members invite-group-hover-card-members--ruled">
            <span className="invite-group-hover-card-members-label">
              {t('conversations.invites.previewRecentMembers', 'Recent Members')}
            </span>
            <div className="invite-group-hover-card-members-list">
              {recentMembers.map((member: GroupInvitePreviewMember) => (
                <div key={member.id} className="invite-group-hover-card-member">
                  <div className="invite-group-hover-card-member-avatar">
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt="" className="invite-group-hover-card-member-avatar-img" />
                    ) : (
                      <span className="invite-group-hover-card-member-avatar-placeholder">
                        {member.displayName.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="invite-group-hover-card-member-name">
                    {member.displayName}
                    {member.isAdmin && (
                      <span className="conversation-member-admin-badge">
                        {t('conversations.admin', 'Admin')}
                      </span>
                    )}
                  </span>
                  <span className="invite-group-hover-card-member-username">
                    @{member.username}
                  </span>
                </div>
              ))}
              {extraGroupMembers > 0 && (
                <div className="invite-group-hover-card-overflow">
                  {t('conversations.invites.moreMembers', {
                    count: extraGroupMembers,
                    defaultValue: `+${extraGroupMembers} more`,
                  })}
                </div>
              )}
            </div>
          </div>
          {shownInvited.length > 0 && (
            <div className="invite-group-hover-card-members">
              <span className="invite-group-hover-card-members-label">
                {t('conversations.invites.alsoInvited', 'Also Invited')}
              </span>
              <div className="invite-group-hover-card-members-list">
                {shownInvited.map((member: GroupInvitePreviewMember) => (
                  <div key={member.id} className="invite-group-hover-card-member">
                    <div className="invite-group-hover-card-member-avatar">
                      {member.avatarUrl ? (
                        <img src={member.avatarUrl} alt="" className="invite-group-hover-card-member-avatar-img" />
                      ) : (
                        <span className="invite-group-hover-card-member-avatar-placeholder">
                          {member.displayName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="invite-group-hover-card-member-name">
                      {member.displayName}
                    </span>
                    <span className="invite-group-hover-card-member-username">
                      @{member.username}
                    </span>
                  </div>
                ))}
                {extraInvited > 0 && (
                  <div className="invite-group-hover-card-overflow">
                    {t('conversations.invites.othersInvited', {
                      count: extraInvited,
                      defaultValue: `+${extraInvited} others invited`,
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {!livePreview && (
        <>
          <div className="invite-group-hover-card-header">
            <span className="invite-group-hover-card-type">
              {t('conversations.invites.typeGroup', 'Group')}
            </span>
            <span className="invite-group-hover-card-name">{snapshotName}</span>
            <span className="invite-group-hover-card-count">
              {t('conversations.invites.previewMemberCount', {
                count: invite.memberCount,
                defaultValue: '{{count}} members',
              })}
            </span>
          </div>
          <GroupInviteInboxHoverFacts invite={invite} />
          {snapshotInviterName && (
            <div className="invite-group-hover-card-inviter">
              <span className="invite-group-hover-card-inviter-label">
                {t('conversations.invites.invitedByLabel', 'Invited by')}
              </span>
              <span className="invite-group-hover-card-inviter-name">{snapshotInviterName}</span>
            </div>
          )}
        </>
      )}
      {livePreview && !loading && !preview && (
        <div className="invite-group-hover-card-error">
          {t('conversations.invites.previewUnavailable', 'Preview unavailable')}
        </div>
      )}
    </HoverCard>
  );
}

function InviteSpaceHoverCard({
  invite,
  livePreview,
  children,
}: {
  invite: PublicSpaceInvite;
  livePreview: boolean;
  children: ReactElement;
}) {
  const { t } = useTranslation();
  const { getInvitePreview } = useSpaceInvites();
  const { participantProfiles } = useConversations();
  const { getCipherKey } = useCipherStore();
  const [preview, setPreview] = useState<SpaceInvitePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  const handleOpen = useCallback(async () => {
    if (!livePreview || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    const data = await getInvitePreview(invite.id);
    setPreview(data);
    setLoading(false);
  }, [getInvitePreview, invite.id, livePreview]);

  const localCipherId = preview ? getSpaceCipherLink(preview.spaceId) : null;
  const cipher = localCipherId ? getCipherKey(localCipherId) : null;
  const spaceMeta = preview
    ? {
        id: preview.spaceId,
        name: preview.spaceName ?? '',
        slug: preview.spaceSlug ?? '',
        encryptIdentity: preview.encryptIdentity,
        encryptedName: preview.encryptedName,
        nameNonce: preview.nameNonce,
        cipherId: preview.cipherId,
        description: preview.description,
        encryptedDescription: preview.encryptedDescription,
        descriptionNonce: preview.descriptionNonce,
        logoUrl: preview.logoUrl,
      }
    : null;
  const displayName = spaceMeta
    ? resolveSpaceDisplayName(spaceMeta, cipher, {
        encryptedSpace: t('spaces.encryptedSpacePlaceholder'),
      })
    : null;
  const description = spaceMeta ? resolveSpaceDisplayDescription(spaceMeta, cipher) : undefined;
  const extraMembers = preview ? Math.max(0, preview.memberCount - preview.members.length) : 0;
  const snapshotName = invite.spaceName ?? t('spaces.encryptedSpacePlaceholder');
  const snapshotInviter = invite.invitedByIdentityId
    ? participantProfiles[invite.invitedByIdentityId]
    : undefined;
  const snapshotInviterName = snapshotInviter?.displayName ?? snapshotInviter?.username;

  return (
    <HoverCard
      trigger={children}
      positioning={{ placement: 'right', gutter: 8 }}
      className="invite-group-hover-card"
      openDelay={300}
      closeDelay={200}
      onOpenChange={(details: { open: boolean }) => {
        if (details.open) void handleOpen();
      }}
    >
      {livePreview && loading && (
        <div className="invite-group-hover-card-loading">
          <span className="spinner spinner-sm" />
        </div>
      )}
      {livePreview && !loading && preview && (
        <>
          <div className="invite-group-hover-card-header">
            <SpaceInviteTypeLabel
              visibility={preview.visibility}
              className="invite-group-hover-card-type"
              iconClassName="invite-group-hover-card-type-icon"
            />
            <span className="invite-group-hover-card-name">
              {spaceMeta && (
                <SpaceLogo
                  space={spaceMeta}
                  name={displayName ?? snapshotName}
                  size="sm"
                  className="invite-group-hover-card-logo"
                />
              )}
              <span className="invite-group-hover-card-name-text">{displayName}</span>
              {isSpaceInviteEncrypted(preview) && (
                <span className="invite-group-hover-card-encrypted">
                  <Icon name="lock" className="invite-group-hover-card-encrypted-icon" />
                  {t('spaces.encrypted', 'Encrypted')}
                </span>
              )}
            </span>
            <span className="invite-group-hover-card-count">
              {t('spaces.memberCount', { count: preview.memberCount })}
            </span>
            <SpaceInviteInboxExpiry invite={invite} className="invite-group-hover-card-expiry" />
          </div>
          {description && (
            <p className="invite-group-hover-card-description">{description}</p>
          )}
          <SpaceInviteInboxHoverFacts invite={invite} />
          <div className="invite-group-hover-card-inviter">
            <span className="invite-group-hover-card-inviter-label">
              {t('conversations.invites.invitedByLabel', 'Invited by')}
            </span>
            <span className="invite-group-hover-card-inviter-name">
              {preview.invitedBy.displayName}
              {preview.invitedBy.isAdmin && (
                <span className="conversation-member-admin-badge">
                  {t('conversations.admin', 'Admin')}
                </span>
              )}
            </span>
          </div>
          <div className="invite-group-hover-card-members invite-group-hover-card-members--ruled">
            <span className="invite-group-hover-card-members-label">
              {t('conversations.invites.previewRecentMembers', 'Recent Members')}
            </span>
            <div className="invite-group-hover-card-members-list">
              {preview.members.map((member: SpaceInvitePreviewMember) => (
                <div key={member.id} className="invite-group-hover-card-member">
                  <div className="invite-group-hover-card-member-avatar">
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt="" className="invite-group-hover-card-member-avatar-img" />
                    ) : (
                      <span className="invite-group-hover-card-member-avatar-placeholder">
                        {member.displayName.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="invite-group-hover-card-member-name">
                    {member.displayName}
                    {member.isAdmin && (
                      <span className="conversation-member-admin-badge">
                        {t('conversations.admin', 'Admin')}
                      </span>
                    )}
                  </span>
                  <span className="invite-group-hover-card-member-username">
                    @{member.username}
                  </span>
                </div>
              ))}
              {extraMembers > 0 && (
                <div className="invite-group-hover-card-overflow">
                  {t('spaces.invites.inbox.moreMembers', { count: extraMembers })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {!livePreview && (
        <>
          <div className="invite-group-hover-card-header">
            <SpaceInviteTypeLabel
              visibility={invite.visibility}
              className="invite-group-hover-card-type"
              iconClassName="invite-group-hover-card-type-icon"
            />
            <span className="invite-group-hover-card-name">
              <span className="invite-group-hover-card-name-text">{snapshotName}</span>
              {isSpaceInviteEncrypted(invite) && (
                <span className="invite-group-hover-card-encrypted">
                  <Icon name="lock" className="invite-group-hover-card-encrypted-icon" />
                  {t('spaces.encrypted', 'Encrypted')}
                </span>
              )}
            </span>
            <span className="invite-group-hover-card-count">
              {t('spaces.memberCount', { count: invite.memberCount })}
            </span>
            <SpaceInviteInboxExpiry invite={invite} className="invite-group-hover-card-expiry" />
          </div>
          <SpaceInviteInboxHoverFacts invite={invite} />
          {snapshotInviterName && (
            <div className="invite-group-hover-card-inviter">
              <span className="invite-group-hover-card-inviter-label">
                {t('conversations.invites.invitedByLabel', 'Invited by')}
              </span>
              <span className="invite-group-hover-card-inviter-name">{snapshotInviterName}</span>
            </div>
          )}
        </>
      )}
      {livePreview && !loading && !preview && (
        <div className="invite-group-hover-card-error">
          {t('conversations.invites.previewUnavailable', 'Preview unavailable')}
        </div>
      )}
    </HoverCard>
  );
}

const INBOX_BUCKET_LABEL: Record<InviteInboxBucket, string> = {
  pending: 'invitationInbox.bucketPending',
  declined: 'invitationInbox.bucketDeclined',
  expired: 'invitationInbox.bucketExpired',
  accepted: 'invitationInbox.bucketAccepted',
};

const INBOX_EMPTY_COPY: Record<InviteInboxBucket, string> = {
  pending: 'invitationInbox.emptyPending',
  declined: 'invitationInbox.emptyDeclined',
  expired: 'invitationInbox.emptyExpired',
  accepted: 'invitationInbox.emptyAccepted',
};

const INBOX_SORT_LABEL: Record<InviteInboxSort, string> = {
  name: 'invitationInbox.sortName',
  createdAt: 'invitationInbox.sortRecency',
  expiresAt: 'invitationInbox.sortExpiration',
};

function InvitationInboxToolbar() {
  const { t } = useTranslation();
  const { bucket, sort, dir, counts, selectBucket, selectSort, selectDir } = useInvitationInbox();

  return (
    <div className="sidebar-invitations-panel-toolbar">
      <label className="sidebar-invitations-panel-bucket-label">
        <select
          className="sidebar-invitations-panel-bucket"
          value={bucket}
          aria-label={t('invitationInbox.statusAria')}
          data-testid="invite-inbox-bucket"
          onChange={(event) => selectBucket(event.target.value as InviteInboxBucket)}
        >
          {INVITE_INBOX_BUCKETS.map((option) => (
            <option key={option} value={option}>
              {t(INBOX_BUCKET_LABEL[option], { count: counts[option] })}
            </option>
          ))}
        </select>
      </label>
      <Menu.Root
        positioning={{ placement: 'bottom-end', gutter: 4 }}
        onSelect={(details) => {
          const value = details.value;
          if ((INVITE_INBOX_SORTS as readonly string[]).includes(value)) {
            selectSort(value as InviteInboxSort);
            return;
          }
          if (value === 'asc' || value === 'desc') {
            selectDir(value as InviteInboxSortDir);
          }
        }}
      >
        <Tooltip content={t('invitationInbox.filterAria')}>
          <Menu.Trigger asChild>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="sidebar-invitations-panel-filter"
              aria-label={t('invitationInbox.filterAria')}
            >
              <Icon name="filter" />
            </Button>
          </Menu.Trigger>
        </Tooltip>
        <Portal>
          <Menu.Positioner className="sidebar-invitations-sort-menu-positioner">
            <Menu.Content className="dm-context-menu sidebar-invitations-sort-menu">
              {INVITE_INBOX_SORTS.map((option) => (
                <Menu.Item
                  key={option}
                  value={option}
                  className="dm-context-menu-item"
                  data-testid={`invite-sort-${option}`}
                >
                  <span className="sidebar-invitations-sort-check" aria-hidden>
                    {sort === option ? <Icon name="check" /> : null}
                  </span>
                  {t(INBOX_SORT_LABEL[option])}
                </Menu.Item>
              ))}
              <Menu.Separator className="dm-context-menu-separator" />
              <Menu.Item
                value="asc"
                className="dm-context-menu-item"
                data-testid="invite-sort-asc"
              >
                <span className="sidebar-invitations-sort-check" aria-hidden>
                  {dir === 'asc' ? <Icon name="check" /> : null}
                </span>
                {t('invitationInbox.sortAsc')}
              </Menu.Item>
              <Menu.Item
                value="desc"
                className="dm-context-menu-item"
                data-testid="invite-sort-desc"
              >
                <span className="sidebar-invitations-sort-check" aria-hidden>
                  {dir === 'desc' ? <Icon name="check" /> : null}
                </span>
                {t('invitationInbox.sortDesc')}
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </div>
  );
}

function InvitationInboxPager() {
  const { t } = useTranslation();
  const { page, total, totalPages, setPage } = useInvitationInbox();
  if (total <= INVITE_INBOX_PAGE_SIZE) return null;

  return (
    <div className="sidebar-invitations-panel-pager" data-testid="invite-inbox-pager">
      <Button
        variant="ghost"
        size="sm"
        type="button"
        disabled={page <= 1}
        onClick={() => setPage(page - 1)}
      >
        {t('common.previous')}
      </Button>
      <span className="sidebar-invitations-panel-pager-label">
        {t('invitationInbox.page', { page, totalPages })}
      </span>
      <Button
        variant="ghost"
        size="sm"
        type="button"
        disabled={page >= totalPages}
        onClick={() => setPage(page + 1)}
      >
        {t('common.next')}
      </Button>
    </div>
  );
}

export function ChatInvitationsPanel({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { acceptInvite, declineInvite, participantProfiles, setActiveConversation } = useConversations();
  const {
    accept: acceptSpaceInvite,
    decline: declineSpaceInvite,
  } = useSpaceInvites();
  const {
    bucket,
    items,
    counts,
    refresh,
  } = useInvitationInbox();
  const navigate = useNavigate();
  const toast = useToast();
  const { closeMobile } = useSidebar();
  const [processingInvite, setProcessingInvite] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const pending = bucket === 'pending';

  const pendingKind = invitationNoticeKind(counts.pendingGroup, counts.pendingSpace);
  const pendingTotal = counts.pendingGroup + counts.pendingSpace;
  const panelTitle = pendingKind
    ? t(invitationNoticeI18nKey(pendingKind), { count: pendingTotal })
    : t('nav.inviteHistory');

  const handleAccept = useCallback(
    async (inviteId: string, conversationId: string, event: ReactMouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setProcessingInvite(inviteId);
      const accepted = await acceptInvite(inviteId);
      setProcessingInvite(null);
      await refresh();
      if (accepted) {
        setActiveConversation(conversationId);
        navigate(`/conversations/${conversationId}`);
        onClose();
        closeMobile();
      }
    },
    [acceptInvite, refresh, setActiveConversation, navigate, onClose, closeMobile]
  );

  const handleDecline = useCallback(
    async (inviteId: string, event: ReactMouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setProcessingInvite(inviteId);
      await declineInvite(inviteId);
      setProcessingInvite(null);
      await refresh();
    },
    [declineInvite, refresh]
  );

  const handleAcceptSpace = useCallback(
    async (inviteId: string, event: ReactMouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setProcessingInvite(inviteId);
      const outcome = await acceptSpaceInvite(inviteId);
      setProcessingInvite(null);
      await refresh();

      if (outcome.needsCipher) {
        toast.error(t('spaces.joinModal.cipherMissingRequired'));
        return;
      }
      if (!outcome.ok) {
        toast.error(outcome.error ?? t('spaces.joinError'));
        return;
      }

      // Only a slug from the accept response routes anywhere.
      const path = spacePathFromSlug(outcome.slug);
      if (path) navigate(path);
      onClose();
      closeMobile();
    },
    [acceptSpaceInvite, closeMobile, navigate, onClose, refresh, t, toast],
  );

  const handleDeclineSpace = useCallback(
    async (inviteId: string, event: ReactMouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setProcessingInvite(inviteId);
      await declineSpaceInvite(inviteId);
      setProcessingInvite(null);
      await refresh();
    },
    [declineSpaceInvite, refresh],
  );

  useSidebarPanelDismiss({
    isOpen,
    onClose,
    panelRef,
    // Sort menu (and invite hover cards) portal to body, so a click on them
    // is outside panelRef. Closing on that mousedown would unmount the menu
    // before onSelect can apply the sort.
    ignoreClosestSelector: '.hover-card-content, .dm-context-menu, .sidebar-invitations-sort-menu, .sidebar-invitations-sort-menu-positioner',
  });

  if (!isOpen) return null;

  return (
    <div className="sidebar-invitations-panel" ref={panelRef}>
      <div className="sidebar-invitations-panel-header">
        <span className="sidebar-invitations-panel-title">{panelTitle}</span>
        <Button
          variant="ghost"
          size="sm"
          className="sidebar-invitations-panel-close"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <Icon name="x" />
        </Button>
      </div>

      <InvitationInboxToolbar />

      <div className="sidebar-invitations-panel-list">
        {items.map((item) => {
          if (item.kind === 'group') {
            const invite = item.invite;
            const inviterProfile = participantProfiles[invite.invitedByIdentityId];
            const inviterName = inviterProfile?.displayName ?? inviterProfile?.username;
            const isProcessing = processingInvite === invite.id;

            const othersCount = invite.memberCount - 1;
            const displayName = invite.hasGroupName
              ? t('conversations.invites.groupNameHidden', 'Conversation topic hidden')
              : inviterName
                ? (othersCount > 0
                  ? t('conversations.invites.inviterAndOthers', {
                      name: inviterName,
                      count: othersCount,
                      defaultValue: `${inviterName} + ${othersCount} others`,
                    })
                  : t('conversations.invites.inviterGroup', {
                      name: inviterName,
                      defaultValue: `${inviterName}'s Group`,
                    }))
                : t('conversations.invites.group', 'Group');

            const typeMeta = [
              t('conversations.invites.typeGroup', 'Group'),
              t('conversations.invites.memberCount', {
                count: invite.memberCount,
                defaultValue: `${invite.memberCount} members`,
              }),
            ].join(' · ');

            const row = (
              <div className="sidebar-invitations-panel-item">
                <div className="sidebar-invitations-panel-item-main">
                  <span className="sidebar-invitations-panel-item-name">
                    {displayName}
                  </span>
                  {pending && (
                    <div className="sidebar-invitations-panel-item-actions">
                      <Tooltip content={t('conversations.invites.accept', 'Accept')}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="sidebar-invite-action-btn sidebar-invite-action-accept"
                          onClick={(event) => void handleAccept(invite.id, invite.conversationId, event)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? <span className="spinner spinner-sm" /> : <Icon name="check" />}
                        </Button>
                      </Tooltip>
                      <Tooltip content={t('conversations.invites.decline', 'Decline')}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="sidebar-invite-action-btn sidebar-invite-action-decline"
                          onClick={(event) => void handleDecline(invite.id, event)}
                          disabled={isProcessing}
                          aria-label={t('conversations.invites.decline', 'Decline')}
                        >
                          <Icon name="x" />
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                </div>
                <span className="sidebar-invitations-panel-item-meta">
                  {typeMeta}
                  <GroupInviteInboxInlineFacts invite={invite} />
                </span>
                {inviterName ? (
                  <span className="sidebar-invitations-panel-item-inviter">
                    {t('conversations.invites.invitedBy', { name: inviterName, defaultValue: `From ${inviterName}` })}
                  </span>
                ) : null}
              </div>
            );

            return (
              <InviteGroupHoverCard key={invite.id} invite={invite} livePreview={pending}>
                {row}
              </InviteGroupHoverCard>
            );
          }

          const invite = item.invite;
          const inviterProfile = invite.invitedByIdentityId
            ? participantProfiles[invite.invitedByIdentityId]
            : undefined;
          const inviterName = inviterProfile?.displayName ?? inviterProfile?.username;
          const isProcessing = processingInvite === invite.id;
          // An `encryptIdentity` Space sends no plaintext name; the placeholder
          // is the honest label rather than exposing the slug.
          const displayName = invite.spaceName ?? t('spaces.encryptedSpacePlaceholder');
          const encrypted = isSpaceInviteEncrypted(invite);

          const row = (
            <div className="sidebar-invitations-panel-item">
              <div className="sidebar-invitations-panel-item-main">
                <span className="sidebar-invitations-panel-item-name">
                  <span className="sidebar-invitations-panel-item-name-text">{displayName}</span>
                  {encrypted && (
                    <Icon
                      name="lock"
                      className="sidebar-invitations-panel-item-lock"
                      title={t('spaces.encrypted', 'Encrypted')}
                    />
                  )}
                </span>
                {pending && (
                  <div className="sidebar-invitations-panel-item-actions">
                    <Tooltip content={t('conversations.invites.accept', 'Accept')}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="sidebar-invite-action-btn sidebar-invite-action-accept"
                        onClick={(event) => void handleAcceptSpace(invite.id, event)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? <span className="spinner spinner-sm" /> : <Icon name="check" />}
                      </Button>
                    </Tooltip>
                    <Tooltip content={t('conversations.invites.decline', 'Decline')}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="sidebar-invite-action-btn sidebar-invite-action-decline"
                        onClick={(event) => void handleDeclineSpace(invite.id, event)}
                        disabled={isProcessing}
                        aria-label={t('conversations.invites.decline', 'Decline')}
                      >
                        <Icon name="x" />
                      </Button>
                    </Tooltip>
                  </div>
                )}
              </div>
              <SpaceInviteMeta invite={invite} inviterName={inviterName} />
              <SpaceInviteInboxRowFacts invite={invite} />
            </div>
          );

          return (
            <InviteSpaceHoverCard key={invite.id} invite={invite} livePreview={pending}>
              {row}
            </InviteSpaceHoverCard>
          );
        })}

        {items.length === 0 && (
          <div className="sidebar-invitations-panel-empty">
            {t(INBOX_EMPTY_COPY[bucket])}
          </div>
        )}
      </div>

      <InvitationInboxPager />
    </div>
  );
}
